import { useMutation, useQueryClient } from '@tanstack/react-query';
import type {
  PickupActionResponse,
  PickupCompleteRequest,
} from '@/lib/api-admin/types';
import {
  ApiClientError,
  idempotencyKeyFor,
  TransportError,
  type ApiError as ApiErrorPayload,
  type ApiMeta,
} from '@/lib/http-core';
import { apiClient } from '@/lib/apiClient';

interface ErrorEnvelopeShape {
  success: false;
  error: ApiErrorPayload;
  meta?: ApiMeta;
}

const throwIfError = (raw: unknown, status: number): never => {
  const env = raw as ErrorEnvelopeShape | null | undefined;
  if (env && env.success === false && env.error) {
    throw new ApiClientError(env.error, {
      status,
      ...(env.meta ? { meta: env.meta } : {}),
    });
  }
  throw new TransportError(`Pickup confirm failed with status ${status}`, raw);
};

const COMPLETE_PATH = '/api/admin/v1/pickups/{pickupId}/complete';

export interface OrderPickupConfirmInput {
  pickupId: string;
  body: PickupCompleteRequest;
}

export const useOrderPickupConfirmMutation = () => {
  const qc = useQueryClient();
  return useMutation<PickupActionResponse, Error, OrderPickupConfirmInput>({
    mutationFn: async ({ pickupId, body }) => {
      const concretePath = COMPLETE_PATH.replace('{pickupId}', pickupId);
      const key = idempotencyKeyFor('POST', concretePath, body);
      const { data, error, response } = await apiClient.raw.POST(COMPLETE_PATH, {
        body: body as never,
        params: {
          path: { pickupId },
          header: { 'Idempotency-Key': key },
        },
      });
      if (!data || data.success !== true) throwIfError(error ?? data, response.status);
      return (data as { data: PickupActionResponse }).data;
    },
    onSuccess: async () => {
      await qc.invalidateQueries({ queryKey: ['admin', 'orders'] });
    },
  });
};
